import { Building2, MapPin, Zap, Image as ImageIcon } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { money, date, humanise } from '@/lib/format';
import {
  Badge, Card, CardHeader, DetailRow, EmptyState, PageHeader,
} from '@/components/ui';
import { PhotoGrid } from '@/components/Files';
import { usePortal } from './PortalShell';

const BILLING_EXPLAIN: Record<string, string> = {
  included_in_rent: 'Included in your rent',
  split_equally: 'Shared equally between tenants',
  per_room_meter: 'Metered for your room',
  billed_to_landlord: 'Paid by the landlord',
  prepaid_by_tenant: 'You buy this yourself',
};

export default function PortalProperty() {
  const { data } = usePortal();
  const { currency } = useAuth();

  if (!data.lease || !data.property) {
    return (
      <Card>
        <EmptyState
          icon={<Building2 className="h-6 w-6" />}
          title="No active tenancy"
          message="Once you are assigned to a room, its details appear here."
        />
      </Card>
    );
  }

  const address = [
    data.property.address_line1,
    data.property.address_line2,
    data.property.city,
    data.property.state_region,
    data.property.postal_code,
    data.property.country,
  ].filter(Boolean).join(', ');

  return (
    <div className="space-y-6">
      <PageHeader
        title={data.room?.name ?? data.property.name}
        subtitle={
          <span className="flex items-center gap-2 flex-wrap">
            <span>{data.room ? data.property.name : humanise(data.property.type)}</span>
            {address && (
              <>
                <span className="text-faint">·</span>
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />{address}
                </span>
              </>
            )}
          </span>
        }
      />

      {data.gallery.length > 0 && (
        <Card>
          <CardHeader title="Photos" />
          <div className="p-5 sm:p-6 pt-4">
            <PhotoGrid files={data.gallery} readOnly />
          </div>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title="Your tenancy" />
          <div className="p-5 sm:p-6 pt-4">
            <dl>
              <DetailRow label="Rent">
                {money(data.lease.rent_cents, currency)} / {humanise(data.lease.cycle)}
              </DetailRow>
              <DetailRow label="Due each period">Day {data.lease.due_day}</DetailRow>
              <DetailRow label="Started">{date(data.lease.start_date)}</DetailRow>
              <DetailRow label="Ends">
                {data.lease.end_date ? date(data.lease.end_date) : 'Open-ended'}
              </DetailRow>
              <DetailRow label="Deposit held">{money(data.lease.deposit_held_cents, currency)}</DetailRow>
              {data.room && (
                <>
                  <DetailRow label="Room type">{data.room.room_type ?? '—'}</DetailRow>
                  <DetailRow label="Floor">{data.room.floor ?? '—'}</DetailRow>
                  <DetailRow label="Maximum occupants">{data.room.max_occupants}</DetailRow>
                </>
              )}
            </dl>
          </div>
        </Card>

        <Card>
          <CardHeader title="What's included" subtitle="Facilities at this property" />
          <div className="p-5 sm:p-6 pt-4 space-y-5">
            {data.room && data.room.facilities.length > 0 && (
              <div>
                <p className="label mb-2">Your room</p>
                <div className="flex flex-wrap gap-2">
                  {data.room.facilities.map((f) => <Badge key={f} tone="brand">{f}</Badge>)}
                </div>
              </div>
            )}

            {data.property.facilities.length > 0 ? (
              <div>
                <p className="label mb-2">The building</p>
                <div className="flex flex-wrap gap-2">
                  {data.property.facilities.map((f) => <Badge key={f} tone="accent">{f}</Badge>)}
                </div>
              </div>
            ) : (
              !data.room?.facilities.length && (
                <p className="text-sm text-faint">No facilities listed.</p>
              )
            )}
          </div>
        </Card>
      </div>

      {data.utilities.length > 0 && (
        <Card>
          <CardHeader
            title="Services & utilities"
            subtitle="How each one is paid for"
          />
          <div className="p-5 sm:p-6 pt-4">
            <div className="grid gap-3 sm:grid-cols-2">
              {data.utilities.map((u) => (
                <div key={u.name} className="flex items-start gap-3 p-3 rounded-xl border border-line bg-raised/40">
                  <div className="h-9 w-9 rounded-lg bg-surface border border-line flex items-center
                                  justify-center shrink-0 text-muted">
                    <Zap className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium truncate">{u.name}</p>
                    <p className="text-xs text-faint truncate">{u.provider ?? humanise(u.category)}</p>
                    <p className="text-xs text-muted mt-1">
                      {BILLING_EXPLAIN[u.billing_mode] ?? humanise(u.billing_mode)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {data.gallery.length === 0 && (
        <Card>
          <EmptyState
            icon={<ImageIcon className="h-6 w-6" />}
            title="No photos shared yet"
            message="Your landlord has not added photos of this property."
          />
        </Card>
      )}
    </div>
  );
}
